import { requestIdleCallback } from '@theme/utilities';
import { isComponentTag } from '@theme/registry';

/**
 * Base class for this theme's custom elements.
 *
 * Ported from Shopify's Horizon theme (`assets/component.js`), © Shopify Inc.,
 * used under the theme license in LICENSE.md.
 *
 * Two deliberate changes from the original, both documented in
 * `docs/component-architecture.md`:
 *
 *   1. **No declarative shadow DOM.** Horizon's `DeclarativeShadowElement` base
 *      and every `roots` / ShadowRoot branch is gone. This theme styles by class
 *      from a 238 KB global sheet, and classes do not cross a shadow boundary.
 *      Adding it back the day it is needed is a small change; carrying it now
 *      would be 54 lines of code nothing runs.
 *   2. **Tag recognition goes through the registry**, not through a
 *      `-component` suffix. Our tags are named for their role
 *      (`countdown-timer`, `cart-drawer`), so Horizon's `endsWith('-component')`
 *      heuristic would never match and the upgrade guard below would be dead
 *      code. See `registry.js` for why that check lives in a third file.
 *
 * What the class gives a component:
 *
 *   - `this.refs.name` for every `ref="name"` descendant (`ref="name[]"` for a
 *     list), kept in sync by a MutationObserver as the subtree changes.
 *   - `requiredRefs`, which fails loudly at connect time instead of handing you
 *     an `undefined` three functions later.
 *   - Declarative events: `on:click="/method"` in the markup, resolved through a
 *     single delegated listener per event type for the whole document.
 *   - A real lifecycle: `connectedCallback` fires wherever the markup came from
 *     — server render, Section Rendering API, `cloneNode`, the theme editor.
 *
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * @template {Refs} [T=Refs]
 * @extends {HTMLElement}
 */
export class Component extends HTMLElement {
  /**
   * References to descendants carrying a `ref` attribute.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * Refs the component cannot work without. Missing ones throw at connect time.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  connectedCallback() {
    registerEventListeners();

    this.#updateRefs();

    requestIdleCallback(() => {
      this.#mutationObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['ref'],
        attributeOldValue: true,
      });
    });
  }

  /**
   * Called when the Section Rendering API re-renders this element and morph detects a change in
   * its subtree. morph skips subtrees it finds unchanged (oldNode.isEqualNode(newNode)), so this
   * does not fire when a re-render leaves this component's own subtree identical.
   */
  updatedCallback() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  /**
   * Rebuilds `refs` from the current DOM.
   */
  #updateRefs() {
    const refs = /** @type any */ ({});

    for (const element of this.querySelectorAll('[ref]')) {
      // A nested component owns its own refs; they must not leak into ours.
      if (!this.#isDescendant(element)) continue;

      const refName = element.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(element);
        refs[path] = array;
      } else {
        refs[path] = element;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }

  /**
   * Watches the subtree so refs survive whatever rewrites it.
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
      )
    ) {
      this.#updateRefs();
    }
  });

  /**
   * Whether a node belongs to this component rather than to a nested one.
   *
   * @param {Node} node
   * @returns {boolean}
   */
  #isDescendant = (node) => getClosestComponent(node.parentNode) === this;
}

/**
 * Walks up to the closest element that is — or is meant to be — a `Component`.
 *
 * The "meant to be" half matters: between the moment `bootstrap.js` fires the
 * dynamic import and the moment that module executes, the element is in the DOM
 * but is still an `HTMLElement`. Without the registry check the walk would step
 * straight past it and hand the event to an outer component, which is worse than
 * dropping it.
 *
 * @param {Node | null} node - The starting node.
 * @returns {HTMLElement | null}
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (isUpgradableComponent(node)) return /** @type {HTMLElement} */ (node);

  return getClosestComponent(node.parentNode);
}

/**
 * Whether a node is a custom element this theme defines as a `Component` but
 * which has not been upgraded yet.
 *
 * Two signals, because neither covers the other: the registry knows tags whose
 * module has not loaded (so `customElements.get` is still undefined), and
 * `customElements.get` knows components defined outside the registry.
 *
 * @param {Node} node
 * @returns {boolean}
 */
function isUpgradableComponent(node) {
  if (!(node instanceof HTMLElement)) return false;

  const tag = node.tagName.toLowerCase();
  if (!tag.includes('-')) return false;
  if (isComponentTag(tag)) return true;

  const constructor = customElements.get(tag);

  return constructor ? constructor.prototype instanceof Component : false;
}

/**
 * Installs one delegated listener per event type, for the whole document.
 *
 * `on:click="/method"` calls `method` on the closest component.
 * `on:click="selector/method"` calls it on the closest matching ancestor, or on
 * `document.querySelector(selector)` when the selector is an id.
 */
let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  const events = [
    'click',
    'change',
    'select',
    'focus',
    'blur',
    'submit',
    'input',
    'keydown',
    'keyup',
    'toggle',
    // `pointerdown` bubbles, so a press landing on a descendant of the
    // `on:pointerdown` element still resolves to it via `closest()`.
    'pointerdown',
  ];
  const shouldBubble = ['focus', 'blur'];
  const expensiveEvents = ['pointerenter', 'pointerleave'];

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);

        if (!element) return;

        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
                get(target, property) {
                  if (property === 'target') return element;

                  const value = Reflect.get(target, property);

                  if (typeof value === 'function') {
                    return value.bind(target);
                  }

                  return value;
                },
              })
            : event;

        const value = element.getAttribute(attribute) ?? '';
        let [selector, method] = value.split('/');
        // Extract the last segment of the attribute value delimited by `?` or `/`
        // Do not use lookback for Safari 16.0 compatibility
        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;
        const instance = selector
          ? selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
          : getClosestComponent(element);

        if (!method) return;

        // Close the custom-element upgrade race: the resolved instance may be an
        // element that's already in the DOM but whose module hasn't executed yet,
        // in which case `instance instanceof Component` is false and the event
        // would be silently dropped. `customElements.upgrade` is synchronous,
        // idempotent, and a no-op when the class isn't registered yet.
        if (!(instance instanceof Component) && isUpgradableComponent(/** @type {Node} */ (instance))) {
          customElements.upgrade(/** @type {HTMLElement} */ (instance));

          if (!(instance instanceof Component)) {
            // Surface the drop instead of swallowing it: on a slow connection
            // this is a real lost interaction, and a silent one is the worst
            // kind to debug.
            console.warn(
              `[component] Dropped "${event.type}" on <${/** @type {HTMLElement} */ (
                instance
              ).tagName.toLowerCase()}> — module not loaded yet`
            );
            return;
          }
        }

        if (!(instance instanceof Component)) return;

        method = method.replace(/\?.*/, '');

        const callback = /** @type {any} */ (instance)[method];

        if (typeof callback === 'function') {
          try {
            /** @type {(Event | Data)[]} */
            const args = [proxiedEvent];

            if (data) args.unshift(parseData(data));

            callback.call(instance, ...args);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { capture: true }
    );
  }

  /** @param {Event} event */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;

    if (!(target instanceof Element)) return;

    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }

    if (expensiveEvents.includes(event.type)) {
      return null;
    }

    return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
  }
}

/**
 * Parses a string to extract data based on a delimiter.
 *
 * @param {string} str - The string to parse.
 * @returns {Object|Array<string|number>|string} The parsed data.
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
      )
    : parseValue(data);
}

/**
 * @typedef {Object|Array<string|number>|string} Data
 */

/**
 * Parses a string value to its appropriate type.
 *
 * @param {string} str - The string to parse.
 * @returns {Data} The parsed value.
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

/**
 * Throws a formatted error when a required ref is not found in the component.
 */
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}
